import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  WalletIcon,
} from "@heroicons/react/24/outline";
import nintendoSearchService from "../../service/nintendoSearch.service";
import { pickCards } from "../../utils/pickCards";
import BuyGuide from "./BuyGuide";
import CardsCheckout from "./CardsCheckout";
import RegionSelect from "./RegionSelect";

const SEARCH_PLACEHOLDER = "Найти игру: «зельда», «ведьмак», «марио кролики»…";
const REGION_CURRENCY = {
  us: "USD",
  ca: "CAD",
  br: "BRL",
  fr: "EUR",
  pl: "PLN",
  se: "SEK",
  hk: "HKD",
};

const panelClass =
  "rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#2a2a2a]";

function money(value, currency) {
  if (value == null) return "";
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  } catch {
    return `${value} ${currency || ""}`.trim();
  }
}

const faceLabel = (value, currency) =>
  money(value, currency).replace(/[,.]00(?=\D*$)/, "");
const rub = (value) => `${Math.round(value).toLocaleString("ru-RU")} ₽`;
const dateRu = (value) =>
  new Date(value).toLocaleDateString("ru", { day: "numeric", month: "long" });
const parseAmount = (text) =>
  Number(String(text).replace(",", ".").replace(/[^\d.]/g, "")) || 0;
const roundCents = (value) => Math.round(value * 100) / 100;

function plural(n, one, few, many) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function regionsNote(data) {
  const labels = (data?.regions || []).map((item) => item.label).join(", ");
  return labels
    ? `Поиск по играм и подбор «Остаток в 0» пока работают для регионов: ${labels}.`
    : "Для этого региона поиск пока недоступен.";
}

function Cover({ src, className }) {
  return src ? (
    <img
      src={src}
      alt=""
      loading="lazy"
      className={`shrink-0 rounded bg-gray-100 object-cover ${className}`}
    />
  ) : (
    <div className={`shrink-0 rounded bg-gray-200 dark:bg-gray-700 ${className}`} />
  );
}

/** Выбранная игра с ценой из прайс-API eShop; при сбое или чужой валюте — прежняя цена каталога. */
function withLivePrice(picked, live) {
  if (typeof live?.price !== "number" || live.currency !== picked.context.currency) {
    return { ...picked, live: { status: "failed" } };
  }
  const was = picked.game.price;
  const changed = was == null || Math.abs(live.price - was) >= 0.005;
  return {
    ...picked,
    game: {
      ...picked.game,
      price: live.price,
      original: live.original,
      percent: live.percent,
      endsAt: live.endsAt,
      rub: changed ? null : picked.game.rub,
    },
    live: changed ? { status: "changed", was } : { status: "same" },
  };
}

function SpendToggle({ on, onChange, className }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      title="Подобрать игры, чтобы потратить остаток баланса без остатка"
      className={`shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl px-3 text-sm font-medium transition ${
        on
          ? "bg-indigo-600 text-white shadow-sm hover:bg-indigo-500"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
      } ${className}`}
    >
      <WalletIcon className="h-4 w-4" />
      Остаток в 0
    </button>
  );
}

function Switch2Badge() {
  return (
    <span className="ml-1 rounded bg-red-600 px-1 align-middle text-[10px] font-semibold text-white">
      Switch 2
    </span>
  );
}

function PriceLine({ game, currency }) {
  if (game.price == null) {
    return <span className="text-gray-400">цены пока нет</span>;
  }
  return (
    <span className="flex flex-wrap items-baseline gap-x-1.5">
      <b className="text-gray-900 dark:text-white">{money(game.price, currency)}</b>
      {game.original != null ? (
        <>
          <span className="text-xs text-gray-400 line-through">
            {money(game.original, currency)}
          </span>
          <span className="text-xs font-semibold text-red-600">−{game.percent}%</span>
        </>
      ) : null}
      {game.rub ? (
        <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
          ≈ {rub(game.rub)}
        </span>
      ) : null}
    </span>
  );
}

function PickedGame({
  game,
  context,
  live,
  catalog,
  initialOpen = false,
  onRefreshCards,
  balance,
  onBalance,
  onSpendLeft,
}) {
  const { currency, label, cardRegion, cards } = context;
  const [buyOpen, setBuyOpen] = useState(initialOpen);
  const canCheckout = Boolean(catalog?.denominations.length) && catalog.currency === currency;
  const denominations = canCheckout ? catalog.denominations : cards?.denominations;
  const hasPrice = game.price != null;
  const balanceValue = parseAmount(balance);
  const need = hasPrice ? Math.max(0, roundCents(game.price - balanceValue)) : 0;
  const plan = useMemo(
    () => (need > 0 && denominations ? pickCards(need, denominations) : null),
    [need, denominations],
  );
  const codesLabel = plan
    ? `${plan.codes} ${plural(plan.codes, "код", "кода", "кодов")}`
    : "";
  const left = plan ? roundCents(balanceValue + plan.face - game.price) : 0;
  const title = game.seoPath ? (
    <a href={game.seoPath} className="hover:underline">
      {game.title}
    </a>
  ) : (
    game.title
  );

  return (
    <div className={`${panelClass} mt-3 p-4`}>
      <div className="flex gap-4">
        <Cover src={game.image} className="h-24 w-24 sm:h-28 sm:w-28" />
        <div className="min-w-0 flex-1">
          <div className="text-lg font-semibold leading-snug text-gray-900 dark:text-white">
            {title}
            {game.switch2 ? <Switch2Badge /> : null}
          </div>
          <div className="mt-2 text-base">
            <PriceLine game={game} currency={currency} />
          </div>
          {game.endsAt ? (
            <div className="mt-1 text-xs text-red-600">скидка до {dateRu(game.endsAt)}</div>
          ) : null}
          {live?.status === "changed" ? (
            <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              {live.was != null
                ? `Цена в eShop сегодня изменилась (была ${money(live.was, currency)}) — карты подобраны по новой.`
                : "Цена взята из eShop прямо сейчас."}
            </div>
          ) : live?.status === "same" ? (
            <div className="mt-1 text-xs text-gray-400">цена сверена с eShop</div>
          ) : null}
          {game.seoPath ? (
            <a
              href={game.seoPath}
              className="mt-1 inline-block text-xs text-indigo-600 hover:underline dark:text-indigo-400"
            >
              где сейчас дешевле →
            </a>
          ) : null}
        </div>
      </div>

      {hasPrice ? (
        <div className="mt-4 border-t border-gray-200 pt-3 text-sm dark:border-gray-700">
          {need === 0 ? (
            <div className="rounded-lg bg-green-50 px-3 py-2 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              Баланса хватает — карты не нужны
            </div>
          ) : plan ? (
            <>
              <div className="text-gray-600 dark:text-gray-300">
                {balanceValue > 0 ? `Не хватает ${money(need, currency)}. ` : ""}
                Подойдут карты eShop {label}:
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {plan.items.map((item) => (
                  <span
                    key={item.face}
                    className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 dark:border-indigo-700 dark:bg-indigo-900/40"
                  >
                    <b>{faceLabel(item.face, currency)}</b>
                    {item.quantity > 1 ? ` × ${item.quantity}` : ""}
                  </span>
                ))}
                <span className="font-semibold">= {rub(plan.rub)}</span>
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {left > 0
                  ? `После покупки на балансе останется ${money(left, currency)}`
                  : "Сумма совпадает с ценой игры без остатка"}
                {left >= 1 ? (
                  <>
                    {" · "}
                    <button
                      type="button"
                      onClick={() => onSpendLeft(left)}
                      className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      подобрать игры на остаток
                    </button>
                  </>
                ) : null}
              </div>
            </>
          ) : !denominations?.length ? (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
              Каталог карт сейчас недоступен — не получилось подобрать карты. Попробуйте
              обновить страницу чуть позже.
            </div>
          ) : (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              Не хватает карт в наличии на {money(need, currency)}
            </div>
          )}

          <label className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            Мой баланс eShop:
            <span className="inline-flex items-center gap-1">
              <input
                inputMode="decimal"
                value={balance}
                placeholder="0"
                onChange={(e) => onBalance(e.target.value.replace(/[^\d.,]/g, ""))}
                className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-800 outline-none focus:border-indigo-500 dark:border-gray-600 dark:bg-[#222222] dark:text-gray-100"
              />
              {currency}
            </span>
            <span className="text-gray-400">необязательно, для точного расчёта</span>
          </label>

          {plan && canCheckout ? (
            <>
              <button
                type="button"
                onClick={() => setBuyOpen((current) => !current)}
                className="mt-3 flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                {buyOpen ? "Скрыть оформление" : `Купить ${codesLabel} за ${rub(plan.rub)}`}
              </button>
              {buyOpen ? <CardsCheckout plan={plan} onPriceChanged={onRefreshCards} /> : null}
            </>
          ) : (
            <Link
              to={`/store/nintendo?region=${cardRegion}`}
              className="mt-3 flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              Купить карты eShop {label}
            </Link>
          )}
          <BuyGuide label={label} />
        </div>
      ) : (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          В eShop {label} цены пока нет — игра ещё не вышла или не продаётся в этом
          регионе.
        </p>
      )}
    </div>
  );
}

function SpendResults({ data, amount, region, label }) {
  if (!data) return null;
  const note = (text) => (
    <p className="px-1 text-sm text-gray-500 dark:text-gray-400">{text}</p>
  );
  if (data.error) return note("Подбор временно недоступен, попробуйте позже.");
  if (data.supported === false) return note(regionsNote(data));
  if (data.ready === false) {
    return note("Собираем цены каталога для этого региона — загляните чуть позже.");
  }
  const currency = data.currency;
  if (data.max && (amount < data.min || amount > data.max)) {
    return note(
      `Введите сумму от ${money(data.min, currency)} до ${money(data.max, currency)}.`,
    );
  }
  if (!data.results?.length) return note("Не нашлось сочетаний на эту сумму.");

  return (
    <div className="space-y-2">
      {data.results.map((variant) => (
        <SpendVariant
          key={variant.items.map((item) => item.nsuid).join("-")}
          variant={variant}
          currency={currency}
          region={region}
          label={label}
        />
      ))}
    </div>
  );
}

const ESHOP_LANG = { us: "en", ca: "en", br: "pt", fr: "fr", pl: "pl", se: "sv", hk: "zh" };

function eshopUrl(item, region) {
  if (item.url) return item.url;
  return `https://ec.nintendo.com/${region.toUpperCase()}/${ESHOP_LANG[region] || "en"}/titles/${item.nsuid}`;
}

/** Открывает страницы игр в eShop; window.open без noopener — иначе не узнать, что вкладку заблокировали. */
function openTabs(items, region) {
  return items.filter((item) => {
    const tab = window.open(eshopUrl(item, region), "_blank");
    if (!tab) return true;
    tab.opener = null;
    return false;
  });
}

function SpendVariant({ variant, currency, region, label }) {
  const [blocked, setBlocked] = useState(null);

  const buy = () => setBlocked(openTabs(variant.items, region));
  const openOne = (item) =>
    setBlocked((current) => current.filter((other) => other.nsuid !== item.nsuid));

  return (
    <div className={`${panelClass} px-3 py-2`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex shrink-0 -space-x-3">
          {variant.items.map((item) => (
            <Cover
              key={item.nsuid}
              src={item.image}
              className="h-10 w-10 border-2 border-white dark:border-[#2a2a2a]"
            />
          ))}
        </div>
        <div className="min-w-0 flex-1 text-sm leading-snug">
          {variant.items.map((item, index) => (
            <React.Fragment key={item.nsuid}>
              {index > 0 ? <span className="text-gray-400"> + </span> : null}
              <a
                href={item.seoPath || item.url}
                {...(item.seoPath ? {} : { target: "_blank", rel: "noreferrer" })}
                className="hover:underline"
              >
                {item.title}
              </a>
              {item.dlc ? <span className="text-[10px] text-gray-400"> DLC</span> : null}{" "}
              <span className="whitespace-nowrap text-gray-500 dark:text-gray-400">
                {money(item.price, currency)}
              </span>
            </React.Fragment>
          ))}
        </div>
        <div className="flex w-full shrink-0 items-center justify-between gap-1 sm:w-auto sm:flex-col sm:items-end">
          <span
            className={`whitespace-nowrap text-[11px] font-semibold ${
              variant.leftover ? "text-gray-500" : "text-green-600"
            }`}
          >
            {variant.leftover
              ? `останется ${money(variant.leftover, currency)}`
              : "ровно в ноль"}
          </span>
          <button
            type="button"
            onClick={buy}
            title={`Откроет ${
              variant.items.length > 1 ? "страницы игр" : "страницу игры"
            } в eShop${label ? ` (${label})` : ""} в новых вкладках`}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-indigo-700"
          >
            Перейти к покупке
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {blocked?.length ? (
        <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          <p>
            Браузер открыл {variant.items.length - blocked.length} из {variant.items.length}{" "}
            вкладок. Откройте остальные кнопками ниже или разрешите всплывающие окна для
            сайта, чтобы в следующий раз всё открывалось одним нажатием.
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {blocked.map((item) => (
              <a
                key={item.nsuid}
                href={eshopUrl(item, region)}
                target="_blank"
                rel="noreferrer"
                onClick={() => openOne(item)}
                className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 font-medium text-gray-900 hover:border-indigo-400 dark:border-amber-700 dark:bg-[#2a2a2a] dark:text-white"
              >
                {item.title}
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-gray-400" />
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Строка поиска с регионом и переключателем «Остаток в 0»: выключен — поиск игр, включён — подбор на остаток. */
function NintendoGameSearch({
  region,
  regions = [],
  onRegionChange,
  cards: cardsApi,
  externalPick,
}) {
  const [spendMode, setSpendMode] = useState(false);
  const [values, setValues] = useState({ search: "", spend: "" });
  const [search, setSearch] = useState(null);
  const [spend, setSpend] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [spendLoading, setSpendLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [balance, setBalance] = useState("");
  const [dlc, setDlc] = useState(false);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  const query = useDebounced(values.search.trim(), 200);
  const spendText = useDebounced(values.spend, 350);
  const amount = parseAmount(spendText);
  const currency = REGION_CURRENCY[region] || "USD";

  useEffect(() => {
    setPicked(null);
    setBalance("");
  }, [region]);

  useEffect(() => {
    if (!externalPick) return;
    setSpendMode(false);
    setOpen(false);
    setPicked(externalPick);
    boxRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [externalPick]);

  const liveNsuid = picked && !picked.live ? picked.game.nsuid : null;
  const liveRegion = picked?.context.region;
  useEffect(() => {
    if (!liveNsuid || !liveRegion) return undefined;
    const controller = new AbortController();
    const apply = (live) =>
      setPicked((current) =>
        current && !current.live && current.game.nsuid === liveNsuid
          ? withLivePrice(current, live)
          : current,
      );
    nintendoSearchService
      .livePrice(liveRegion, liveNsuid, controller.signal)
      .then(apply)
      .catch((error) => {
        if (error.name !== "AbortError") apply(null);
      });
    return () => controller.abort();
  }, [liveNsuid, liveRegion]);

  useEffect(() => {
    if (spendMode || query.length < 2) {
      setSearch(null);
      setSearchLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setSearchLoading(true);
    nintendoSearchService
      .search(region, query, controller.signal)
      .then((data) => {
        setSearch({ ...data, region });
        setOpen(true);
      })
      .catch((error) => {
        if (error.name !== "AbortError") setSearch({ error: true, region });
      })
      .finally(() => {
        if (!controller.signal.aborted) setSearchLoading(false);
      });
    return () => controller.abort();
  }, [query, region, spendMode]);

  useEffect(() => {
    if (!spendMode || amount <= 0) {
      setSpend(null);
      setSpendLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setSpendLoading(true);
    nintendoSearchService
      .spend(region, amount, dlc, controller.signal)
      .then((data) => setSpend({ ...data, region }))
      .catch((error) => {
        if (error.name !== "AbortError") setSpend({ error: true, region });
      })
      .finally(() => {
        if (!controller.signal.aborted) setSpendLoading(false);
      });
    return () => controller.abort();
  }, [amount, region, dlc, spendMode]);

  useEffect(() => {
    const close = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const switchMode = (on, spendValue) => {
    setSpendMode(on);
    if (spendValue != null) {
      setValues((current) => ({ ...current, spend: spendValue }));
    }
    setOpen(false);
    inputRef.current?.focus();
  };

  const pick = (game) => {
    setPicked({
      game,
      context: {
        region: search.region,
        currency: search.currency,
        label: search.label,
        cardRegion: search.cardRegion,
        cards: search.cards,
      },
    });
    setOpen(false);
  };

  const regionSpend = spend?.region === region ? spend : null;
  const meta = (spendMode ? spendLoading : searchLoading)
    ? spendMode
      ? "подбираем…"
      : "ищем…"
    : spendMode && regionSpend?.results?.length
      ? `${money(amount, spend.currency)} · ${spend.results.length} ${plural(
          spend.results.length,
          "вариант",
          "варианта",
          "вариантов",
        )} · цены сверены с eShop`
      : "";

  const ModeIcon = spendMode ? WalletIcon : MagnifyingGlassIcon;

  return (
    <section ref={boxRef} className="mx-auto mt-5 max-w-3xl px-1">
      <div className="relative">
        <div className="flex items-stretch rounded-2xl border border-gray-200 bg-white shadow-sm transition focus-within:border-indigo-400 focus-within:ring-4 focus-within:ring-indigo-100 dark:border-gray-600 dark:bg-[#2a2a2a] dark:focus-within:ring-indigo-900/50">
          {regions.length > 1 ? (
            <RegionSelect
              value={region}
              regions={regions}
              onChange={(value) => onRegionChange?.(value)}
            />
          ) : null}
          <ModeIcon className="ml-3 h-5 w-5 shrink-0 self-center text-gray-400" />
          <input
            ref={inputRef}
            value={spendMode ? values.spend : values.search}
            onChange={(e) =>
              setValues((current) => ({
                ...current,
                [spendMode ? "spend" : "search"]: e.target.value,
              }))
            }
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
            inputMode={spendMode ? "decimal" : "text"}
            autoComplete="off"
            aria-label={spendMode ? "Остаток на балансе eShop" : "Поиск игры"}
            placeholder={
              spendMode
                ? `Остаток на балансе в ${currency}, например 12.37`
                : SEARCH_PLACEHOLDER
            }
            className="min-w-0 flex-1 bg-transparent px-3 py-3.5 text-base text-gray-900 outline-none placeholder:text-gray-400 dark:text-white"
          />
          <SpendToggle on={spendMode} onChange={switchMode} className="m-1.5 hidden sm:flex" />
        </div>

        {!spendMode && open && search?.region === region ? (
        <div className={`${panelClass} absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden`}>
          {search.error ? (
            <p className="px-3 py-3 text-sm text-gray-500">
              Поиск временно недоступен, попробуйте позже.
            </p>
          ) : search.supported === false ? (
            <p className="px-3 py-3 text-sm text-gray-500">{regionsNote(search)}</p>
          ) : search.results.length ? (
            search.results.map((game) => (
              <button
                key={game.nsuid}
                type="button"
                onClick={() => pick(game)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-indigo-50 dark:hover:bg-gray-700"
              >
                <Cover src={game.image} className="h-12 w-12" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-gray-900 dark:text-white">
                    {game.title}
                    {game.switch2 ? <Switch2Badge /> : null}
                  </span>
                  <span className="block text-sm">
                    <PriceLine game={game} currency={search.currency} />
                  </span>
                </span>
              </button>
            ))
          ) : (
            <p className="px-3 py-3 text-sm text-gray-500">
              Ничего не нашлось. Попробуйте английское название или другое написание.
            </p>
          )}
        </div>
        ) : null}
      </div>

      <div className="mt-2 flex min-h-[1.5rem] flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-gray-400">
        <SpendToggle on={spendMode} onChange={switchMode} className="flex py-1.5 sm:hidden" />
        {spendMode ? (
          <label className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400">
            <input
              type="checkbox"
              checked={dlc}
              onChange={(e) => setDlc(e.target.checked)}
            />
            с дополнениями (DLC)
          </label>
        ) : (
          <span className="hidden sm:inline">
            «Остаток в 0» подберёт игры, чтобы потратить остаток баланса eShop полностью
          </span>
        )}
        <span className="ml-auto">{meta}</span>
      </div>

      {spendMode ? (
        <div className="mt-1">
          <SpendResults
            data={regionSpend}
            amount={amount}
            region={region}
            label={regions.find((item) => item.value === region)?.label}
          />
        </div>
      ) : picked ? (
        <PickedGame
          key={`${picked.game.nsuid || picked.game.title}-${picked.context.cardRegion}`}
          game={picked.game}
          context={picked.context}
          live={picked.live}
          catalog={cardsApi?.cardsFor(picked.context.cardRegion)}
          initialOpen={Boolean(picked.openCheckout)}
          onRefreshCards={cardsApi?.refresh}
          balance={balance}
          onBalance={setBalance}
          onSpendLeft={(left) => switchMode(true, String(left))}
        />
      ) : null}
    </section>
  );
}

export default NintendoGameSearch;
